import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UnitFindInterface as UnitFindDto } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { HelpDeskSettingsService } from '@zaoblako/web/core/services/helpdesk/help-desk-settings.service';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { HdTaskStoreService } from '@zaoblako/web/modules/help-desk/components/task/card/services/hd-task-store.service';
import { ChangeClientHelpdeskModalComponent } from '@zaoblako/web/modules/help-desk/dumbs/change-client-helpdesk-modal/change-client-helpdesk-modal.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { ExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-single-select/external-user-single-select.component';
import { HdTaskTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-type-single-select/hd-task-type-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription } from 'rxjs';

export type ClientHelpdeskTaskFormType = {
  client: ClientFindInterface;
  externalUser: ExternalUserFindInterface;
  taskType: HelpdeskTaskTypeFindInterface;
  unit: UnitFindDto;
};

@Component({
  standalone: true,
  selector: 'crm-helpdesk-accept-task-form',
  templateUrl: './helpdesk-accept-task-form.component.html',
  imports: [
    CommonModule,
    ContragentAutoCompleteComponent,
    ExternalUserSingleSelectComponent,
    ButtonModule,
    HdTaskTypeSingleSelectComponent,
    SingleSelectComponent,
    DividerModule,
    SpinnerComponent,
    UnitSingleSelectComponent,
    ClientAutoCompleteComponent,
    CardModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelpdeskAcceptTaskFormComponent implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public form: ClientHelpdeskTaskFormType = {
    client: null,
    externalUser: null,
    taskType: null,
    unit: null
  };

  public errors: Record<keyof ClientHelpdeskTaskFormType, string> = {
    client: null,
    externalUser: null,
    taskType: null,
    unit: null
  };

  public filters: {
    taskTypes: HelpdeskTaskTypeFindInterface[];
  } = {
    taskTypes: []
  };

  private defaultClient: ClientFindInterface;
  private isShowClientModal = true;

  private clientRef: DynamicDialogRef;
  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    public readonly hdTaskStoreService: HdTaskStoreService,
    private readonly helpDeskSettingsService: HelpDeskSettingsService,
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    private readonly dialogService: DialogService
  ) {}

  public ngOnInit() {
    this.form = this.formStoreService.getState.forms[this.index]?.data?.task;
    this.filters = this.formStoreService.getState.forms[this.index]?.data?.filters;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.helpDeskSettingsService.getDefaultClient().subscribe((client) => {
      this.defaultClient = client;
      this.cdRef.markForCheck();
    });
  }

  public ngOnDestroy() {
    this.clientRef?.close();
    this.formSubscription$?.unsubscribe();
  }

  public acceptTask() {
    this.errors = {
      client: null,
      externalUser: null,
      taskType: null,
      unit: null
    };

    if (!this.form.client && this.hdTaskStoreService.getState?.requiredFields?.client) {
      this.errors.client = 'Выберите клиента';
    }
    if (!this.form.externalUser && this.hdTaskStoreService.getState?.requiredFields?.externalUser) {
      this.errors.externalUser = 'Выберите контакт';
    }
    if (!this.form.taskType && this.hdTaskStoreService.getState?.requiredFields?.taskType) {
      this.errors.taskType = 'Выберите тип заявки';
    }
    if (!this.form.unit && this.hdTaskStoreService.getState?.requiredFields?.unit) {
      this.errors.unit = 'Выберите бизнес единицы';
    }

    if (Object.keys(this.errors).some((key) => this.errors[key])) {
      return;
    }

    if (this.defaultClient?.id === this.form.client?.id && this.isShowClientModal) {
      this.openChangeDefaultClientModal();
    } else {
      this.formStoreService.saveNext(this.form, this.index);
    }
  }

  public openFormCreateExternalUser() {
    const result = this.formStoreService.addForm(TypeFormEnum.EXTERNAL_USER_CREATE, {
      headerText: 'Добавление внешнего пользователя',
      widthSize: 'form-sidebar-small',
      data: {
        client: this.form?.client
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((externalUser: ExternalUserFindInterface) => {
        if (externalUser) {
          this.form.externalUser = externalUser;
          this.cdRef.markForCheck();
        }
      })
    );
  }

  public changeClient() {
    if (this.form.taskType) {
      this.form.taskType = null;
      this.cdRef.markForCheck();
    }

    this.helpDeskTaskTypeService
      .find<HelpdeskTaskTypeFindInterface>({ clientIds: this.form.client?.id ? [this.form.client.id] : [] })
      .subscribe({
        next: (taskTypes) => {
          this.filters.taskTypes = taskTypes;
          this.filters = { ...this.filters };
          this.cdRef.markForCheck();
        }
      });
  }

  private openChangeDefaultClientModal() {
    const taskId = this.hdTaskStoreService.getState.task.id;
    this.clientRef = this.dialogService.open(ChangeClientHelpdeskModalComponent, {
      header: 'Сменить клиента по-умолчанию?',
      contentStyle: { overflow: 'auto' },
      styleClass: 'xl:w-4 lg:w-6 md:w-8 sm:w-11 w-full',
      baseZIndex: 10000,
      data: {
        client: this.form.client,
        externalUser: this.form.externalUser,
        taskId
      }
    });

    this.clientRef.onClose.subscribe(
      (data: { client: ClientFindInterface; externalUser: ExternalUserFindInterface; isShowClientModal: boolean }) => {
        if (data?.client) {
          this.form = { ...this.form, client: data.client, externalUser: data.externalUser };
          this.isShowClientModal = data?.isShowClientModal;
          this.cdRef.markForCheck();
        }
      }
    );
  }
}
